<template>
  <donut-accordion-radio v-model="hrisSelection" :options="optionsWithStatus">
    <template #filter>
      <div class="t m-tx1">{{ `${props.thingsText} employees with the following employment types:` }}</div>
      <div class="f-v f-gap-8 m-tx1">
        <donut-input-checkbox
          v-for="(category, cIdx) in props.hrisCategories"
          :key="cIdx"
          :model-value="category.active"
          :label="category.name"
          :disabled="isDisabled(category)"
          :class="isDisabled(category) ? 'pe-n' : ''"
          @update:model-value="(val) => onCategoryClick(category, val)"
        />
      </div>
    </template>
  </donut-accordion-radio>
</template>
<script setup>
  import { computed, ref } from 'vue';
  import { useStore } from 'vuex';
  import { cloneDeep } from 'lodash';
  import useAccordionSaveStatus from 'composables/accordion-save-status';

  const { dispatch } = useStore();

  const props = defineProps({
    networkMethod: { type: String, default: 'post' },
    updatePath: { type: String, required: true },
    updateArgsKey: { type: String, default: null },
    hrisCategories: { type: Array, required: true },
    thingsText: { type: String, default: 'Enable this for' },
    everyoneDescription: { type: String, default: '' },
    notEligibleText: { type: String, default: 'for this' },
  });

  const hrisOptions = computed(() => [
    {
      title: 'Everyone',
      value: 'everyone',
      description: props.everyoneDescription,
    },
    {
      title: 'Filter by role type from HRIS',
      value: 'filter',
      description: `People not listed in your HRIS will not be eligible ${props.notEligibleText}.`,
    },
  ]);

  const { optionsWithStatus, startOptionSave, completeOptionSave } = useAccordionSaveStatus({ options: hrisOptions });

  // Don't let them deselect every option
  const isDisabled = (category) => props.hrisCategories.filter(c => c.active && c.hris_value !== category.hris_value).length === 0;

  // For keeping track of previous values between accordion selection changes
  const mostRecentHrisCategoriesValues = ref(null);

  const updateArgs = (newCategories) => ({
    updatePath: props.updatePath,
    networkMethod: props.networkMethod,
    argsKey: props.updateArgsKey,
    newCategories,
  });

  const hrisSelection = computed({
    get() {
      const selectionValue = props.hrisCategories.some(c => c.active) ? 'filter' : 'everyone';
      return hrisOptions.value.find(o => o.value === selectionValue);
    },
    set({ value }) {
      if (hrisSelection.value.value === value) return;
      startOptionSave(value);
      let categories = cloneDeep(props.hrisCategories);
      if (value === 'everyone') {
        // First store whatever "Filter" values you're coming from, then just turn everything off.
        mostRecentHrisCategoriesValues.value = cloneDeep(categories);
        categories.forEach((c) => { c.active = false; });
      } else if (value === 'filter') {
        // Default to setting it to be whatever it used to be, otherwise default to everything active
        const cachedCategories = mostRecentHrisCategoriesValues.value;
        if (cachedCategories) {
          categories = cachedCategories;
        } else {
          categories.forEach((c) => { c.active = true; });
        }
      }
      dispatch('hrisRecipientsRuleSelector/updateEnrollmentRule', updateArgs(categories))
          .then(() => completeOptionSave(value))
          // This doesn't clear the save status text. That's fine.
          .catch(() => dispatch('setErrorToast', null, { root: true }));
    },
  });

  const onCategoryClick = (category, val) => {
    if (!isDisabled(category)) {
      startOptionSave('filter');
      const newCategories = cloneDeep(props.hrisCategories);
      newCategories[newCategories.findIndex(c => c.hris_value === category.hris_value)] = { ...category, active: val };
      dispatch('hrisRecipientsRuleSelector/updateEnrollmentRule', updateArgs(newCategories))
          .then(() => completeOptionSave('filter'))
          // This doesn't clear the save status text. That's fine.
          .catch(() => dispatch('setErrorToast', null, { root: true }));
    }
  };
</script>
